import React, { useCallback, useEffect, useState } from 'react';
import cn from 'clsx';
import { ChevronRightIcon } from '@fronton/icons-react';
import { useSearch } from '@okapi/router-react';
import { Section, Typography } from 'common/components';
import { SpecialOffer as SpecialOfferType, useAvailableOffersSelector, useRegionIdSelector } from 'modules/model-json';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { SpecialOfferModal } from './special-offer-modal';
import {
  specialOfferBannerContentDiscount,
  specialOfferBannerContentDiscountPrefix,
  specialOfferBannerContentDiscountText,
  specialOfferBannerContentText,
  specialOfferBannerContentTextWithDiscount,
  specialOfferBannerContentWrapper,
  specialOfferBannerVarianMap,
  specialOfferGrid,
  SpecialOfferBanner,
  specialOfferBannerAddContentDiscount,
  specialOfferBannerWhiteAddContentDiscount,
  specialOfferBannerChevronIcon,
  specialOfferOddGrid,
} from './special-offer-styles';

export type SpecialOfferProps = {
  elementBlock: string;
  onModalBtnClick?: () => void;
};

const appropriateBannerNames = [
  'brand-primary',
  'brand-secondary',
  'secondary-green-dark',
  'secondary-red-primary',
  'secondary-green-primary',
];

export const SpecialOffer: React.FC<SpecialOfferProps> = ({
  elementBlock,
  onModalBtnClick,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeOfferIndex, setActiveOfferIndex] = useState<number>(0);
  const [searchParams] = useSearch();
  const availableOffers: SpecialOfferType[] = useAvailableOffersSelector();
  const regionId = useRegionIdSelector();

  useEffect(() => {
    availableOffers?.forEach(({ name }, index) => {
      if (name && searchParams.offer === name) {
        setActiveOfferIndex(index);
        setIsModalOpen(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getElementName = useCallback((index: number) => {
    let result = '';
    if (availableOffers[index].banner.creative) {
      result += `${availableOffers[index].banner.creative} `;
    }
    if (availableOffers[index].banner.text) {
      result += `${availableOffers[index].banner.text} `;
    }
    result += availableOffers[index].banner.title;

    return result;
  }, [availableOffers]);

  if (!availableOffers || availableOffers.length === 0) {
    return null;
  }

  const handleBannerClick = (index: number) => () => {
    setIsModalOpen(!isModalOpen);
    setActiveOfferIndex(index);
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock,
      elementName: 'Промопредложение',
      elementText: getElementName(index),
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <Section>
      <div className={cn(specialOfferGrid, { [specialOfferOddGrid]: availableOffers.length % 2 !== 0 })}>
        {availableOffers.map(({ banner }, index) => {
          const bannerName = appropriateBannerNames.includes(banner.name)
            ? banner.name
            : 'secondary-green-dark';
          const textColor = bannerName === 'secondary-green-dark' ? 'text-primary-invert' : 'text-primary';
          const withMinusIcon = banner.creative || banner.text[0] === '-' || banner.text[0] === '−';
          const bannerLeftTextWithoutMinus = banner.text.replace('-', '').replace('−', '');

          return (
            <div
              key={createElementKey('special-offer', index)}
              className={cn(specialOfferBannerVarianMap[bannerName][availableOffers.length])}>
              <SpecialOfferBanner
                data-banner="content"
                data-banner-position={index + 1}
                onClick={handleBannerClick(index)}>
                <div className={specialOfferBannerContentWrapper}>
                  {banner.text && (
                    <div
                      className={cn(specialOfferBannerContentDiscount, {
                        [specialOfferBannerAddContentDiscount]: withMinusIcon,
                        [specialOfferBannerWhiteAddContentDiscount]:
                          textColor === 'text-primary-invert' && withMinusIcon,
                      })}
                      data-banner-content="discount-wrapper"
                      data-banner-add-content={withMinusIcon ? 'discount-wrapper-minus' : null}>
                      {banner.creative && (
                        <Typography
                          className={specialOfferBannerContentDiscountPrefix}
                          color={textColor}
                          data-banner-content="prefix"
                          variant="l"
                          size="body_short">
                          до
                        </Typography>
                      )}
                      <Typography
                        className={specialOfferBannerContentDiscountText}
                        color={textColor}
                        data-banner-content="discount"
                        variant="display">
                        {withMinusIcon ? bannerLeftTextWithoutMinus : banner.text}
                      </Typography>
                    </div>
                  )}

                  <Typography
                    className={cn(specialOfferBannerContentText, {
                      [specialOfferBannerContentTextWithDiscount]: banner.text,
                    })}
                    color={textColor}
                    data-banner-content={banner.text ? 'text' : 'only-text'}
                    variant={banner.text ? 'h3' : 'h1'}>
                    {banner.title}
                  </Typography>
                </div>
                <ChevronRightIcon
                  className={specialOfferBannerChevronIcon}
                  size="xxl"
                  color={`var(--${textColor})`}
                  data-banner-content="arrow"
                />
              </SpecialOfferBanner>
            </div>
          );
        })}
      </div>
      {isModalOpen && (
        <SpecialOfferModal
          elementBlock={elementBlock}
          elementName={`${getElementName(activeOfferIndex)} / modal`}
          modalContent={availableOffers[activeOfferIndex].modal}
          isOpen={isModalOpen}
          onModalBtnClick={onModalBtnClick}
          onClose={handleCloseModal}
          regionId={regionId}
        />
      )}
    </Section>
  );
};
